import * as XLSX from 'xlsx';

import {
  CANONICAL_REGIONS,
  coverageSheetName,
  partitionRegionsForLabel,
  priceSheetName,
  type PartitionRegion,
} from './regionKeys';
import type {
  EatonCoverageRow,
  EatonDuplicateRow,
  EatonMissingSpa,
  EatonPriceRow,
} from './types';

const PRICE_HEADERS = [
  'SPA',
  'SPA Description',
  'Region',
  'Coverage Regions',
  'Customer Count',
  'Location',
  'Catalogue',
  'UPC',
  'Description',
  'Description-Fr',
  'Net Price',
  'Source Sheet',
  'File',
] as const;

const COVERAGE_HEADERS = [
  'SPA',
  'SPA Description',
  'Customer Number',
  'Customer Name',
  'Region',
  'Price List Type',
  'Status',
  'Source Sheet',
] as const;

const DUPLICATE_HEADERS = ['Catalogue', 'SPA Count', 'SPAs', 'Prices'] as const;

const MISSING_HEADERS = [
  'SPA',
  'SPA Description',
  'Regions',
  'Customer Count',
] as const;

function priceRow(row: EatonPriceRow): string[] {
  return [
    row.spaId,
    row.spaDescription,
    row.region,
    row.coverageRegions,
    String(row.customerCount),
    row.location,
    row.catalog,
    row.upc,
    row.description,
    row.descriptionFr,
    row.netPrice,
    row.sourceSheet,
    row.fileName,
  ];
}

function coverageRow(row: EatonCoverageRow): string[] {
  return [
    row.spaId,
    row.spaDescription,
    row.customerNumber,
    row.customerName,
    row.region,
    row.priceListType,
    row.status,
    row.sourceSheet,
  ];
}

function appendSheet(
  workbook: XLSX.WorkBook,
  name: string,
  headers: readonly string[],
  rows: string[][],
): void {
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.aoa_to_sheet([[...headers], ...rows]),
    name,
  );
}

function emptyBuckets<T>(): Record<PartitionRegion, T[]> {
  return {
    AB: [],
    BC: [],
    Midwest: [],
    ON: [],
    QC: [],
    Other: [],
  };
}

function bucketByRegion<T extends { region: string }>(
  rows: readonly T[],
  serialize: (row: T) => string[],
): Record<PartitionRegion, string[][]> {
  const buckets = emptyBuckets<string[]>();
  for (const row of rows) {
    const destinations = partitionRegionsForLabel(row.region);
    const serialized = serialize(row);
    for (const region of destinations) {
      buckets[region].push(serialized);
    }
  }
  return buckets;
}

/**
 * Workbook Courtney can open: prices and coverage partitioned by IM region
 * (GEN-2566), plus global duplicates / missing SPAs.
 *
 * Canonical Prices/Coverage tabs (AB, BC, Midwest, ON, QC) are always present
 * even when empty (headers only). Other is omitted when empty.
 */
export function writeExtractedWorkbook(input: {
  prices: readonly EatonPriceRow[];
  coverage: readonly EatonCoverageRow[];
  duplicates: readonly EatonDuplicateRow[];
  missingSpas: readonly EatonMissingSpa[];
}): ArrayBuffer {
  const workbook = XLSX.utils.book_new();
  const priceBuckets = bucketByRegion(input.prices, priceRow);
  const coverageBuckets = bucketByRegion(input.coverage, coverageRow);

  for (const region of CANONICAL_REGIONS) {
    appendSheet(
      workbook,
      priceSheetName(region),
      PRICE_HEADERS,
      priceBuckets[region],
    );
  }
  if (priceBuckets.Other.length > 0) {
    appendSheet(
      workbook,
      priceSheetName('Other'),
      PRICE_HEADERS,
      priceBuckets.Other,
    );
  }

  for (const region of CANONICAL_REGIONS) {
    appendSheet(
      workbook,
      coverageSheetName(region),
      COVERAGE_HEADERS,
      coverageBuckets[region],
    );
  }
  if (coverageBuckets.Other.length > 0) {
    appendSheet(
      workbook,
      coverageSheetName('Other'),
      COVERAGE_HEADERS,
      coverageBuckets.Other,
    );
  }

  appendSheet(
    workbook,
    'Duplicates',
    DUPLICATE_HEADERS,
    input.duplicates.map((row) => [
      row.catalog,
      String(row.spaIds.length),
      row.spaIds.join('; '),
      row.prices.join('; '),
    ]),
  );
  appendSheet(
    workbook,
    'Missing SPAs',
    MISSING_HEADERS,
    input.missingSpas.map((row) => [
      row.spaId,
      row.spaDescription,
      row.regions,
      String(row.customerCount),
    ]),
  );
  return XLSX.write(workbook, {
    type: 'array',
    bookType: 'xlsx',
  }) as ArrayBuffer;
}
